import { useCallback, useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { FxIcon, FxNavShell, FxSearchField, MAIN_ID, type FxNavItem } from '@/components/fx'
import { AccountMenu } from './AccountMenu'
import { CreateTemplateDialog, useCreateTemplateDialog } from './CreateTemplateDialog'
import { ImpersonationBanner } from './ImpersonationBanner'
import { ShellUsageLine } from './ShellUsageLine'
import { VerificationBanner } from './VerificationBanner'
import { PageTitle, ShellChromeProvider, useShellChromeState } from './shellChrome'

/**
 * The frame every signed-in screen sits in: the 80px icon rail from 992px
 * up, the top bar with its drawer below that, the five-slot bottom nav at
 * phone width, and the utility row along the top of the content.
 *
 * One shell with a responsive switch, not two — `Main.dc.html` and
 * `PhoneUebersicht.dc.html` are the same navigation at two widths, and two
 * components would drift apart the first time a destination is added.
 * `FxNavShell` already owns the width switch, the drawer and its focus
 * handling; this is the product's own destinations, routing and chrome
 * hung on it.
 *
 * It is a react-router **layout route**: it renders `<Outlet/>` and nothing
 * about the screen inside it, so `/templates/:templateId` can nest a second
 * layout of its own underneath without this one knowing.
 *
 * Two things are the shell's own because they are the account's and not any
 * screen's: `VerificationBanner` and `ShellUsageLine`. Both are written to
 * render *nothing* where the API does not back them.
 */

/** Where each rail destination goes. `account` is the avatar's own target. */
export const SHELL_ROUTES: Record<string, string> = {
  home: '/',
  projects: '/projekte',
  archive: '/archiv',
  settings: '/einstellungen',
  account: '/einstellungen',
}

/**
 * The destinations, in the order the artboards draw them.
 *
 * `Papierkorb` is **not** among them, although `FX_RAIL_ITEMS` and
 * `FX_BOTTOM_NAV_ITEMS` default to it: there is no soft-delete tier in the
 * schema and no route, so the slot would lead nowhere. The artboards agree —
 * `Main.dc.html` labels the archive item "Archiv und Papierkorb" and the
 * phone nav's fifth slot is Einstellungen.
 */
const RAIL_ITEMS: FxNavItem[] = [
  { key: 'home', icon: 'home', label: 'Übersicht' },
  { key: 'projects', icon: 'folder', label: 'Projekte' },
  { key: 'archive', icon: 'archive', label: 'Archiv' },
]
const RAIL_FOOTER_ITEMS: FxNavItem[] = [
  { key: 'settings', icon: 'settings', label: 'Einstellungen' },
]
const BOTTOM_ITEMS: FxNavItem[] = [
  { key: 'projects', icon: 'folder', label: 'Projekte' },
  { key: 'home', icon: 'home', label: 'Übersicht' },
  { key: 'create', icon: 'add', label: 'Neues Musterformular' },
  { key: 'archive', icon: 'archive', label: 'Archiv' },
  { key: 'settings', icon: 'settings', label: 'Einstellungen' },
]

/**
 * Which rail item is the current one.
 *
 * `/templates/:id` matches none of them on purpose: a form can be opened
 * from the Übersicht, from a project or from the archive, and marking one of
 * those "you are here" would be a guess a screen reader then reads out as
 * fact. No `aria-current` at all is the honest answer.
 */
export function shellNavKey(pathname: string): string {
  if (pathname === '/') return 'home'
  if (pathname === '/projekte' || pathname.startsWith('/projekte/')) return 'projects'
  if (pathname === '/archiv' || pathname.startsWith('/archiv/')) return 'archive'
  if (pathname === '/einstellungen' || pathname.startsWith('/einstellungen/')) return 'settings'
  return ''
}

export function AppShell() {
  const navigate = useNavigate()
  const location = useLocation()
  const active = shellNavKey(location.pathname)
  const chrome = useShellChromeState(location.pathname)
  const [phoneSearchOpen, setPhoneSearchOpen] = useState(false)
  const phoneSearchInput = useRef<HTMLInputElement>(null)

  const onNavigate = useCallback(
    (key: string) => {
      const to = SHELL_ROUTES[key]
      if (to) navigate(to)
    },
    [navigate],
  )

  // "Neues Musterformular" opens the shell's own create dialog, so it works
  // the same on all six shell routes.
  //
  // It used to set `?dialog=upload` on whatever route was open and leave the
  // screen to render a dialog; only the Übersicht ever did, and it answered
  // by sending the person into the pre-account funnel — a sign-up wizard that
  // says "du" — so on the other five routes the button changed the address
  // bar and nothing else. `?dialog=upload` additionally means "Dokumente
  // hinzufügen" inside the cockpit, which is why the dialog has a parameter
  // of its own (`?neu=musterformular`); see `CreateTemplateDialog`.
  const create = useCreateTemplateDialog()
  const onCreate = create.open

  /**
   * Focus moves to the page on every navigation.
   *
   * A client-side route change replaces the whole document below the rail
   * and moves nothing: the keyboard stays on the rail button that was
   * pressed, and a screen reader's virtual cursor stays where it was, so
   * activating "Projekte" appears to do nothing at all. Moving focus to
   * `<main>` is the plain fix — the new screen's heading is read out and the
   * next Tab starts inside it. Not on the first render: an arrival should
   * not have focus taken away from wherever the browser put it.
   */
  const firstRender = useRef(true)
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    document.getElementById(MAIN_ID)?.focus()
  }, [location.pathname])

  // A screen that gives up the search field must not leave the phone's
  // search row open over the next one.
  const hasSearch = chrome.search != null
  useEffect(() => {
    if (!hasSearch) setPhoneSearchOpen(false)
  }, [hasSearch])

  useEffect(() => {
    if (phoneSearchOpen) phoneSearchInput.current?.focus()
  }, [phoneSearchOpen])

  const helpButton = chrome.help && (
    <button
      type="button"
      aria-label={chrome.help.label ?? 'Hilfe zu dieser Seite'}
      className="flex h-11 w-11 flex-none items-center justify-center rounded-sm text-primary-text md:h-control md:w-control"
      onClick={chrome.help.onOpen}
    >
      <FxIcon name="info" size={24} filled />
    </button>
  )

  return (
    <ShellChromeProvider value={chrome}>
      <FxNavShell
        active={active}
        items={RAIL_ITEMS}
        footerItems={RAIL_FOOTER_ITEMS}
        bottomItems={BOTTOM_ITEMS}
        onNavigate={onNavigate}
        onCreate={onCreate}
        accountSlot={<AccountMenu side="right" />}
        className="bg-ground"
        topBarActions={
          <>
            {chrome.search && (
              <button
                type="button"
                aria-label="Suchen"
                aria-expanded={phoneSearchOpen}
                className="flex h-11 w-11 flex-none items-center justify-center rounded-sm text-primary-text"
                onClick={() => setPhoneSearchOpen((open) => !open)}
              >
                <FxIcon name="search" size={28} />
              </button>
            )}
            {helpButton}
            <AccountMenu size={36} />
          </>
        }
      >
        {/* The desktop page gutter is the rail's own `md:pl-17`; this adds
            the artboard's remaining insets (20 top, 52 right, 40 bottom) and
            the phone's 20px side padding. */}
        <div className="flex min-h-full flex-col gap-5 px-5 pb-10 pt-5 md:pl-0 md:pr-[52px]">
          {chrome.search && phoneSearchOpen && (
            <FxSearchField
              ref={phoneSearchInput}
              className="w-full md:hidden"
              placeholder={chrome.search.placeholder}
              value={chrome.query}
              onChange={(event) => chrome.setQuery(event.target.value)}
              onSearch={(value) => chrome.search?.onSearch?.(value)}
            />
          )}

          {/* The utility row. It is `justify-end` and may end up empty —
              there is nothing in it a screen has not claimed, plus the usage
              line, which hides itself when the API reports no figure. */}
          <div className="hidden items-center justify-end gap-6 md:flex">
            <ShellUsageLine />
            {helpButton}
            {chrome.search && (
              <FxSearchField
                placeholder={chrome.search.placeholder}
                value={chrome.query}
                onChange={(event) => chrome.setQuery(event.target.value)}
                onSearch={(value) => chrome.search?.onSearch?.(value)}
              />
            )}
          </div>

          <ImpersonationBanner />
          <VerificationBanner />

          <Outlet />

          {/* The shell's own dialog, so the rail's "+" needs nothing from
              the screen underneath it. */}
          <CreateTemplateDialog />
        </div>
      </FxNavShell>
    </ShellChromeProvider>
  )
}

export { PageTitle }
